/**
 * ConversationTTLPicker — chat-header dropdown for picking how long
 * new messages live before the server's auto-destruct task wipes them.
 *
 * Lifecycle:
 *   - Mounts inline in the chat header next to the Phone/Search buttons.
 *   - Reads the current ttl_hours from the conversation prop (default 24h).
 *   - PUT /api/conversations/{id}/ttl on selection.
 *   - The parent listens for `ttl_updated` WS events and re-fetches /
 *     re-syncs the conversation prop, which causes us to re-render the
 *     new label.
 *
 * Important security note (also encoded in the backend route's docstring):
 *   Changing the TTL only affects messages sent AFTER the change. We do
 *   not retro-rewrite expires_at on existing rows, because:
 *     (a) other participants may already have shown "expires in 23h"
 *         to the user, and silent retro-shortening would itself be a
 *         coercion vector ("I made the room ephemeral after the fact"),
 *     (b) recipient devices have local plaintext copies anyway.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display labels keyed by hours. We pre-compute these so the dropdown
// always shows the *exact* canonical label — picking 24h via the
// dropdown lights up "24 hours" rather than "24.0000 hours".
const TTL_OPTIONS = [
  { hours: 5 / 60, label: '5 minutes', sub: 'burner' },
  { hours: 1, label: '1 hour', sub: 'short fuse' },
  { hours: 24, label: '24 hours', sub: 'default' },
  { hours: 24 * 7, label: '7 days', sub: 'long-running thread' },
  { hours: 24 * 30, label: '30 days', sub: 'maximum' },
];

export function formatTtlLabel(hours) {
  if (hours == null) return '24h';
  const match = TTL_OPTIONS.find((o) => Math.abs(o.hours - hours) < 1e-3);
  if (match) {
    if (match.hours < 1) return `${Math.round(match.hours * 60)}m`;
    if (match.hours <= 24) return `${match.hours}h`;
    return `${Math.round(match.hours / 24)}d`;
  }
  // Custom (non-canonical) value: render compactly.
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours <= 24) return `${hours.toFixed(1)}h`;
  return `${Math.round(hours / 24)}d`;
}

export function ConversationTTLPicker({ conversation, onUpdated }) {
  const currentTtl = Number(conversation?.ttl_hours ?? 24);
  const [busy, setBusy] = useState(false);

  const apply = async (hours) => {
    if (busy) return;
    if (Math.abs(hours - currentTtl) < 1e-3) return; // no-op
    setBusy(true);
    try {
      const res = await axios.put(`${API}/conversations/${conversation.id}/ttl`, {
        ttl_hours: hours,
      });
      onUpdated?.(res.data);
      toast.success(`Auto-destruct → ${formatTtlLabel(hours)}`, {
        duration: 2200,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Could not update TTL';
      toast.error(`TTL update failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1.5 text-slate-400 hover:text-emerald-300 px-2"
                data-testid="ttl-picker-trigger"
                disabled={busy}
                aria-label="Set message auto-destruct timer"
              >
                <Clock className="w-3.5 h-3.5" />
                <span data-testid="ttl-picker-label">
                  {formatTtlLabel(currentTtl)}
                </span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs max-w-xs">
            New messages auto-destruct after this duration. Existing
            messages keep their original timer — TTL changes never
            retro-shorten.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel className="text-xs">
          Auto-destruct timer
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TTL_OPTIONS.map((opt) => {
          const active = Math.abs(opt.hours - currentTtl) < 1e-3;
          return (
            <DropdownMenuItem
              key={opt.hours}
              onClick={() => apply(opt.hours)}
              data-testid={`ttl-option-${formatTtlLabel(opt.hours)}`}
              className={active ? 'bg-emerald-500/10 text-emerald-300' : ''}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{opt.label}</span>
                <span className="text-[10px] text-slate-500">{opt.sub}</span>
              </div>
              {active && (
                <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-400">
                  current
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
